import React, { useMemo, useState, useEffect } from 'react';
import { renderIcon, DefaultCampaignIcon } from '../SprintDetail/FlowchartView/utils/renderIcon';
import LogoLoader from '../ui/LogoLoader';
import { getRecentWorkspaceItems } from '../../utils/recentWorkspaceItems';
import { useAuth } from '../AuthProvider';
import './NewTabPage.css';

const MAX_CARDS = 5;

function cardIcon(icon) {
  return renderIcon(icon, {
    size: '36px',
    fallback: <DefaultCampaignIcon size={36} />,
    defaultColor: '#a1a1aa',
  });
}

/**
 * Browser-style New Tab page — Chrome profile-picker layout for recent items.
 */
export default function NewTabPage({
  sprints = [],
  onSelectSprint,
  onSelectTemplate,
  onSelectView,
  openCreateSprintModal,
}) {
  const { workspaceId } = useAuth();
  const [recent, setRecent] = useState(() => getRecentWorkspaceItems(workspaceId));

  useEffect(() => {
    setRecent(getRecentWorkspaceItems(workspaceId));
  }, [workspaceId]);

  const resolvedRecent = useMemo(() => {
    return recent
      .map((item) => {
        if (item.type === 'campaign' || item.type === 'template') {
          const sprint = sprints.find(
            (s) =>
              s.id === item.id &&
              !s.deletedAt &&
              (item.type === 'template'
                ? s.isTemplate || s.type === 'template'
                : !s.isTemplate)
          );
          if (!sprint) return null;
          return {
            ...item,
            label: sprint.name || item.label,
            icon: sprint.icon ?? item.icon,
            entity: sprint,
          };
        }
        return {
          ...item,
          entity: { id: item.id, name: item.label, icon: item.icon },
        };
      })
      .filter(Boolean)
      .slice(0, MAX_CARDS);
  }, [recent, sprints]);

  const handleOpen = (item) => {
    if (item.type === 'campaign') {
      onSelectSprint?.(item.entity);
    } else if (item.type === 'template') {
      onSelectTemplate?.(item.entity);
    } else if (item.type === 'view') {
      onSelectView?.(item.entity);
    }
  };

  const typeLabel = (type) => {
    if (type === 'template') return 'Template';
    if (type === 'view') return 'View';
    return 'Campaign';
  };

  return (
    <div className="new-tab-page">
      <div className="new-tab-page__inner">
        <header className="new-tab-page__header">
          <div className="new-tab-page__logo" aria-hidden>
            <LogoLoader size={52} />
          </div>
          <h1 className="new-tab-page__title">Jump back in</h1>
          <p className="new-tab-page__lede">
            Open a recent campaign below, pick one from the sidebar, or start something new.
          </p>
        </header>

        <div className="new-tab-page__grid" role="list">
          {resolvedRecent.map((item) => (
            <button
              key={`${item.type}:${item.id}`}
              type="button"
              className="new-tab-page__card"
              role="listitem"
              onClick={() => handleOpen(item)}
              title={item.label}
            >
              <span className="new-tab-page__card-name">{item.label}</span>
              <span className="new-tab-page__card-avatar">
                {cardIcon(item.icon)}
              </span>
              <span className="new-tab-page__card-meta">{typeLabel(item.type)}</span>
            </button>
          ))}

          {openCreateSprintModal && (
            <button
              type="button"
              className="new-tab-page__card new-tab-page__card--add"
              role="listitem"
              onClick={() => openCreateSprintModal()}
              title="New Campaign"
            >
              <span className="new-tab-page__card-name">New Campaign</span>
              <span className="new-tab-page__card-avatar new-tab-page__card-avatar--add">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden>
                  <path
                    d="M14 7v14M7 14h14"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <span className="new-tab-page__card-meta">Create</span>
            </button>
          )}
        </div>

        {resolvedRecent.length === 0 && (
          <p className="new-tab-page__empty-hint">
            Nothing recent yet — open a campaign from the sidebar and it’ll show up here.
          </p>
        )}
      </div>
    </div>
  );
}
